import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Save, FileText, Building2, Users, Receipt, BarChart3, Phone, Settings, Plus, Trash2, Sparkles, AlertCircle, ArrowRight, ExternalLink } from 'lucide-react';
import { clientsAPI } from '@/lib/api';
import { toast } from 'sonner';
import DocumentUpload from '@/components/admin/DocumentUpload';
import MCAAutofillTab from '@/pages/admin/clients/MCAAutofillTab';

const ENTITY_TYPES = [
  { value: 'private_limited', label: 'Private Limited Company' },
  { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
  { value: 'partnership', label: 'Partnership Firm' },
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'individual', label: 'Individual' },
];

const FINANCIAL_YEARS = [
  '2024-25', '2023-24', '2022-23', '2021-22', '2020-21'
];

// Entity types that require directors/partners
const ENTITY_WITH_DIRECTORS = ['private_limited', 'llp', 'partnership'];

// Entity types that require CIN/LLPIN
const ENTITY_WITH_CIN = ['private_limited', 'llp'];

// Entity types that require MoA/AoA
const ENTITY_WITH_MOA = ['private_limited'];

export default function AdminClientForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  
  const [formData, setFormData] = useState({
    // Basic Info
    entity_type: 'private_limited',
    entity_name: '',
    status: 'draft',
    
    // Legal Entity Information
    cin: '',
    date_of_incorporation: '',
    registered_address: '',
    legal_observation: '',
    addresses: [],  // [{ id, type, line, is_primary, is_rented }]
    
    // Directors
    directors: [{ name: '', designation: 'Director', din: '', din_status: '', source: 'manual' }],
    director_observation: '',
    
    // Registrations & Tax
    pan: '',
    msme_udyam_id: '',
    gstin: '',
    startup_india_dpiit_number: '',
    fssai_number: '',
    esic_number: '',
    shop_establishment_number: '',
    other_registrations: [],  // [{ label, number }]
    registration_observation: '',
    
    // Financial Documents
    last_completed_fy: '2023-24',
    financial_observation: '',
    // Bank accounts per year (dynamic)
    banks: {
      y1: [{ name: '', account_number: '' }],
      y2: [{ name: '', account_number: '' }],
      y3: [{ name: '', account_number: '' }],
    },
    
    // Contact Information
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_observation: '',
    
    // Tally Prime Setup
    tally_cloud_setup: false,
    chart_of_accounts_setup: false,
    import_trial_balance: false,
    payment_link_setup: false,
    bank_integration_setup: false,
    tally_dashboard_setup: false,
    whatsapp_integration: false,
    gst_filing_setup: false,
    tds_filing_setup: false,
    tally_observation: '',
    
    // Documents tracking
    documents: {},
  });

  useEffect(() => {
    if (isEdit) {
      fetchClient();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [mcaAutofillNotice, setMcaAutofillNotice] = useState({ fields: [] });
  const [setupIssues, setSetupIssues] = useState([]);
  const [showSetupDialog, setShowSetupDialog] = useState(false);

  // MCA sometimes returns DOI as DD/MM/YYYY or DD-MM-YYYY (from XLSX).
  // <input type="date"> only renders ISO YYYY-MM-DD, so normalise before use.
  const toISODate = (s) => {
    if (!s) return '';
    const str = String(s).trim();
    // Already ISO?
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    // DD/MM/YYYY or DD-MM-YYYY
    const m = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const [, d, mo, y] = m;
      return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    // Fallback: try Date.parse
    const t = Date.parse(str);
    if (!Number.isNaN(t)) {
      const iso = new Date(t).toISOString().slice(0, 10);
      return iso;
    }
    return str;
  };

  const fetchClient = async () => {
    try {
      const response = await clientsAPI.getOne(id);
      const data = response.data;
      // Ensure directors is an array
      if (!data.directors || !Array.isArray(data.directors)) {
        data.directors = [];
      }
      // Ensure documents is an object
      if (!data.documents) {
        data.documents = {};
      }
      // Ensure banks is an object with arrays
      if (!data.banks) {
        data.banks = {
          y1: [{ name: '', account_number: '' }],
          y2: [{ name: '', account_number: '' }],
          y3: [{ name: '', account_number: '' }],
        };
      }

      // ─── ONE-TIME MCA AUTO-FILL (Jun 2026) ───
      // Only fill fields the admin hasn't set. Never overwrites existing data.
      const autoFilled = [];
      const mca = data.mca_company_info || {};
      if (!data.date_of_incorporation && mca.date_of_incorporation) {
        data.date_of_incorporation = toISODate(mca.date_of_incorporation);
        autoFilled.push('Date of Incorporation');
      } else if (data.date_of_incorporation) {
        // Normalise legacy values already saved as DD/MM/YYYY so the input renders correctly.
        const iso = toISODate(data.date_of_incorporation);
        if (iso !== data.date_of_incorporation) data.date_of_incorporation = iso;
      }
      if (!data.cin && mca.cin) {
        data.cin = mca.cin;
        autoFilled.push('CIN');
      }
      if (!data.entity_name && mca.company_name) {
        data.entity_name = mca.company_name;
        autoFilled.push('Entity Name');
      }
      // Multi-address: if none set, seed a registered_office row from MCA
      if ((!data.addresses || data.addresses.length === 0)) {
        const seed = [];
        const mcaAddr = mca.registered_address || data.registered_address;
        if (mcaAddr) {
          seed.push({
            id: `addr_${Date.now()}`,
            type: 'registered_office',
            line: mcaAddr,
            is_primary: true,
            is_rented: false,
          });
          if (mca.registered_address && !data.addresses?.length) autoFilled.push('Registered Office Address');
        }
        data.addresses = seed;
      }
      // Auto-seed directors from mca_directors ONE-TIME
      const mcaDirs = data.mca_directors || [];
      const hasMcaSourced = (data.directors || []).some((d) => d.source === 'mca');
      if (mcaDirs.length > 0 && !hasMcaSourced) {
        const seeded = mcaDirs.map((d) => ({
          name: d.name || '',
          designation: d.designation || 'Director',
          din: d.din_pan || d.din || '',
          din_status: d.din_status || '',
          source: 'mca',
        }));
        // Keep any manual entries that were already there
        const manualOnly = (data.directors || []).filter((d) => d.source !== 'mca' && (d.name || '').trim());
        data.directors = [...seeded, ...manualOnly];
        autoFilled.push(`${mcaDirs.length} director(s) w/ DIN + status`);
      }
      if (!data.directors || data.directors.length === 0) {
        data.directors = [{ name: '', designation: 'Director', din: '', din_status: '', source: 'manual' }];
      }
      if (!data.other_registrations) data.other_registrations = [];

      setFormData(prev => ({ ...prev, ...data }));
      if (autoFilled.length > 0) setMcaAutofillNotice({ fields: autoFilled });
    } catch (error) {
      toast.error('Failed to load client');
      navigate('/admin/clients');
    }
  };

  const resetFromMCA = () => {
    // Force re-run of the auto-fill logic by clearing fields locally.
    // (User-friendly "Reset from MCA" affordance shown in the Legal Entity section.)
    setFormData((prev) => ({
      ...prev,
      date_of_incorporation: '',
      cin: '',
      addresses: [],
      directors: (prev.directors || []).filter((d) => d.source !== 'mca'),
    }));
    setTimeout(fetchClient, 50);
    toast.success('Re-fetched from MCA data');
  };

  // Document handlers
  const handleDocumentUpload = (docType, docData) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [docType]: {
          status: 'uploaded',
          file_id: docData.id,
          file_name: docData.file_name,
          uploaded_at: docData.uploaded_at,
          verified_at: null
        }
      }
    }));
  };

  const handleDocumentDelete = (docType) => {
    setFormData(prev => {
      const newDocs = { ...prev.documents };
      delete newDocs[docType];
      return { ...prev, documents: newDocs };
    });
  };

  const handleDocumentVerify = (docType) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [docType]: {
          ...prev.documents[docType],
          status: 'verified',
          verified_at: new Date().toISOString()
        }
      }
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  // Director management
  const addDirector = () => {
    setFormData(prev => ({
      ...prev,
      directors: [...prev.directors, { name: '', designation: 'Director', din: '', din_status: '', source: 'manual' }]
    }));
  };

  const removeDirector = (index) => {
    if (formData.directors.length > 1) {
      setFormData(prev => ({
        ...prev,
        directors: prev.directors.filter((_, i) => i !== index)
      }));
    }
  };

  const updateDirector = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      directors: prev.directors.map((d, i) => 
        i === index ? { ...d, [field]: value } : d
      )
    }));
  };

  // Bank management per year
  const addBank = (yearKey) => {
    setFormData(prev => ({
      ...prev,
      banks: {
        ...prev.banks,
        [yearKey]: [...(prev.banks?.[yearKey] || []), { name: '', account_number: '' }]
      }
    }));
  };

  const removeBank = (yearKey, index) => {
    const banks = formData.banks?.[yearKey] || [];
    if (banks.length > 1) {
      setFormData(prev => ({
        ...prev,
        banks: {
          ...prev.banks,
          [yearKey]: prev.banks[yearKey].filter((_, i) => i !== index)
        }
      }));
    }
  };

  const updateBank = (yearKey, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      banks: {
        ...prev.banks,
        [yearKey]: (prev.banks?.[yearKey] || []).map((b, i) => 
          i === index ? { ...b, [field]: value } : b
        )
      }
    }));
  };

  // ─── Pre-flight readiness check for Mark Complete (Jun 2026) ───
  // Rebuilt after production-drift bug: reads live cap-table state and
  // MCA-directors DIN-status field to compute what's actually outstanding.
  const runPreflightCheck = async () => {
    const issues = [];
    // Cap Table checks — only relevant for Micro CFO retainers
    if (formData.is_micro_cfo) {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ratnatraya_token');
        const res = await fetch(`${API}/api/clients/${id}/shareholding`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const sh = data.shareholding || {};
          if ((sh.share_classes || []).length === 0) {
            issues.push({ key: 'cap-classes', label: 'Cap Table — add at least one share class', target: 'workspace', anchor: 'captable' });
          }
          if ((sh.shareholders || []).length === 0) {
            issues.push({ key: 'cap-holders', label: 'Cap Table — add at least one shareholder', target: 'workspace', anchor: 'captable' });
          }
          if ((sh.allotments || []).length === 0) {
            issues.push({ key: 'cap-allotments', label: 'Cap Table — log at least one allotment', target: 'workspace', anchor: 'captable' });
          }
        }
      } catch (_) { /* if fetch fails, skip cap-table checks — don't block */ }
    }
    // DIN verification check — count MCA directors whose din_status is unpopulated.
    // Per user (Jun 2026): "verified" = din_status field has ANY value (active/inactive/deactivated).
    const mcaDirs = formData.mca_directors || [];
    const unverified = mcaDirs.filter((d) => !((d.din_status || '').toString().trim())).length;
    if (unverified > 0) {
      issues.push({
        key: 'din-unverified',
        label: `${unverified} director(s) still awaiting DIN verification`,
        target: 'workspace',
        anchor: 'mca-directors',
      });
    }
    return issues;
  };

  const navigateToTarget = (issue) => {
    setShowSetupDialog(false);
    if (issue.target === 'workspace' && id) {
      navigate(`/admin/clients/${id}/workspace${issue.anchor ? '#' + issue.anchor : ''}`);
    }
  };

  const handleSubmit = async (e, saveStatus = 'draft') => {
    e.preventDefault();
    
    // Validation for draft - only COI/entity_name required
    if (saveStatus === 'draft' && !formData.entity_name) {
      toast.error('Entity Name is required to save as draft');
      return;
    }
    
    // Validation for complete - all mandatory fields
    if (saveStatus === 'complete') {
      const requiredFields = ['entity_name', 'entity_type', 'contact_name', 'contact_email', 'contact_phone'];
      const missingFields = requiredFields.filter(f => !formData[f]);
      if (missingFields.length > 0) {
        toast.error('Please fill all required fields to mark as complete');
        return;
      }
      // CIN validation for company entity types
      const cinRequired = ['private_limited', 'public_limited', 'opc', 'section_8'].includes(formData.entity_type);
      if (cinRequired && formData.cin && !/^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(formData.cin.trim().toUpperCase())) {
        toast.error('Invalid CIN format. Expected: L/U + 5 digits + 2 letters (state) + 4 digits (year) + 3 letters (type) + 6 digits');
        return;
      }
      // Pre-flight readiness check (Cap Table + DIN verification)
      if (isEdit) {
        const issues = await runPreflightCheck();
        if (issues.length > 0) {
          setSetupIssues(issues);
          setShowSetupDialog(true);
          return;
        }
      }
    }

    setLoading(true);
    const submitData = { ...formData, status: saveStatus };

    try {
      if (isEdit) {
        await clientsAPI.update(id, submitData);
        toast.success(`Client ${saveStatus === 'complete' ? 'completed' : 'saved as draft'} successfully`);
      } else {
        await clientsAPI.create(submitData);
        toast.success(`Client ${saveStatus === 'complete' ? 'created' : 'saved as draft'} successfully`);
      }
      navigate('/admin/clients');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    { icon: Sparkles, title: 'MCA Auto-fill', id: 'autofill' },
    { icon: Building2, title: 'Legal Entity', id: 'legal' },
    { icon: Users, title: 'Directors', id: 'directors', hidden: !ENTITY_WITH_DIRECTORS.includes(formData.entity_type) },
    { icon: Receipt, title: 'Registrations & Tax', id: 'registrations' },
    { icon: BarChart3, title: 'Financial Documents', id: 'financial' },
    { icon: Phone, title: 'Contact', id: 'contact' },
    { icon: Settings, title: 'Tally Setup', id: 'tally' },
  ].filter(s => !s.hidden);

  const showDirectors = ENTITY_WITH_DIRECTORS.includes(formData.entity_type);
  const showCIN = ENTITY_WITH_CIN.includes(formData.entity_type);
  const showMoA = ENTITY_WITH_MOA.includes(formData.entity_type);
  const directorLabel = formData.entity_type === 'private_limited' ? 'Director' : 'Partner';

  return (
    <div className="p-6 lg:p-8" data-testid="admin-client-form">
      {/* Header */}
      <div className="mb-6">
        <Link to="/admin/clients" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft size={18} />
          Back to Clients
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isEdit ? 'Edit Client' : 'Add New Client'}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {formData.status === 'draft' ? '📝 Draft' : '✅ Complete'}
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              type="button" 
              variant="outline"
              onClick={(e) => handleSubmit(e, 'draft')}
              disabled={loading}
            >
              Save as Draft
            </Button>
            <Button 
              type="button"
              className="bg-[#059669] hover:bg-[#047857]"
              onClick={(e) => handleSubmit(e, 'complete')}
              disabled={loading}
            >
              <Save size={18} className="mr-2" />
              Mark Complete
            </Button>
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(index)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeSection === index
                  ? 'bg-[#E84C3D] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Icon size={16} />
              {section.title}
            </button>
          );
        })}
      </div>

      <form onSubmit={(e) => handleSubmit(e, formData.status)}>
        {/* Section 0: MCA Auto-fill (NEW) */}
        {activeSection === 0 && (
          <MCAAutofillTab clientId={id} />
        )}

        {/* Section 1: Legal Entity Information */}
        {activeSection === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="text-[#E84C3D]" size={20} />
                Legal Entity Information
              </CardTitle>
              <CardDescription>Basic company registration details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ── ONE-TIME MCA auto-fill notice ── */}
              {mcaAutofillNotice.fields.length > 0 && (
                <div className="p-2 bg-emerald-50 border border-emerald-200 rounded flex items-start justify-between gap-3 text-xs" data-testid="mca-autofill-notice">
                  <div className="flex items-start gap-2 text-emerald-800">
                    <Sparkles className="w-3.5 h-3.5 mt-0.5" />
                    <span>
                      Auto-filled from MCA data:&nbsp;
                      <strong>{mcaAutofillNotice.fields.join(', ')}</strong>
                    </span>
                  </div>
                  <Button type="button" size="sm" variant="outline" className="h-6 text-[10px]"
                    onClick={resetFromMCA} data-testid="reset-from-mca-btn">
                    Reset from MCA
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Legal Entity Type *</Label>
                  <Select value={formData.entity_type} onValueChange={(v) => handleSelectChange('entity_type', v)}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Name of Entity/Client *</Label>
                  <Input
                    name="entity_name"
                    value={formData.entity_name}
                    onChange={handleChange}
                    placeholder="ABC Enterprises Pvt Ltd"
                    className="h-11"
                    required
                  />
                </div>
              </div>

              {showCIN && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{formData.entity_type === 'llp' ? 'LLPIN' : 'CIN'}</Label>
                    <Input
                      name="cin"
                      value={formData.cin}
                      onChange={handleChange}
                      placeholder={formData.entity_type === 'llp' ? 'AAA-0000' : 'U12345MH2020PTC123456'}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Date of Incorporation
                      {formData.mca_company_info?.date_of_incorporation
                        && toISODate(formData.mca_company_info.date_of_incorporation) === formData.date_of_incorporation
                        && formData.date_of_incorporation && (
                          <span className="ml-2 text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" data-testid="doi-from-mca-chip">
                            From MCA
                          </span>
                        )}
                    </Label>
                    <Input
                      name="date_of_incorporation"
                      type="date"
                      value={formData.date_of_incorporation}
                      onChange={handleChange}
                      className="h-11"
                      data-testid="date-of-incorporation-input"
                    />
                  </div>
                </div>
              )}

              {showCIN && (
                <DocumentUpload
                  clientId={id}
                  docType="coi"
                  label="COI (Certificate of Incorporation)"
                  documentData={formData.documents?.coi}
                  onUploadSuccess={handleDocumentUpload}
                  onDeleteSuccess={handleDocumentDelete}
                  onVerifySuccess={handleDocumentVerify}
                />
              )}

              {/* ── Multi-address list (Jun 2026) ── */}
              <div className="space-y-2" data-testid="addresses-block">
                <div className="flex items-center justify-between">
                  <Label>Office Addresses</Label>
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => setFormData((f) => ({
                      ...f,
                      addresses: [...(f.addresses || []), {
                        id: `addr_${Date.now()}`, type: 'branch_office', line: '',
                        is_primary: false, is_rented: false,
                      }],
                    }))}
                    data-testid="add-address-btn">
                    <Plus size={12} className="mr-1" /> Add Address
                  </Button>
                </div>
                {(formData.addresses || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No addresses added yet. Registered office auto-populates from MCA data.</p>
                ) : (
                  <div className="space-y-2">
                    {formData.addresses.map((addr, idx) => (
                      <div key={addr.id || idx} className="p-3 border rounded bg-slate-50 space-y-2" data-testid={`address-row-${idx}`}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <Select value={addr.type}
                            onValueChange={(v) => setFormData((f) => ({
                              ...f,
                              addresses: f.addresses.map((a, i) => i === idx ? { ...a, type: v } : a),
                            }))}>
                            <SelectTrigger className="h-9 text-xs" data-testid={`address-type-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="registered_office">Registered Office</SelectItem>
                              <SelectItem value="corporate_office">Corporate Office</SelectItem>
                              <SelectItem value="branch_office">Branch Office</SelectItem>
                              <SelectItem value="manufacturing_unit">Manufacturing Unit</SelectItem>
                              <SelectItem value="godown">Godown</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <label className="flex items-center gap-2 text-xs text-slate-600">
                            <input type="checkbox" checked={!!addr.is_rented}
                              onChange={(e) => setFormData((f) => ({
                                ...f,
                                addresses: f.addresses.map((a, i) => i === idx ? { ...a, is_rented: e.target.checked } : a),
                              }))}
                              data-testid={`address-rented-${idx}`} />
                            Rented / Leased
                          </label>
                          <div className="flex justify-end">
                            <Button type="button" size="sm" variant="ghost" className="text-rose-500"
                              onClick={() => setFormData((f) => ({
                                ...f,
                                addresses: f.addresses.filter((_, i) => i !== idx),
                              }))}
                              data-testid={`address-remove-${idx}`}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        <Textarea placeholder="Full address"
                          value={addr.line || ''}
                          onChange={(e) => setFormData((f) => ({
                            ...f,
                            addresses: f.addresses.map((a, i) => i === idx ? { ...a, line: e.target.value } : a),
                          }))}
                          className="min-h-[60px] text-xs"
                          data-testid={`address-line-${idx}`}
                        />
                        {addr.is_rented && (
                          <div data-testid={`address-rent-doc-${idx}`}>
                            <DocumentUpload
                              clientId={id}
                              docType={`rent_agreement_${addr.id || idx}`}
                              label="Rent / Lease Agreement"
                              documentData={formData.documents?.[`rent_agreement_${addr.id || idx}`]}
                              onUploadSuccess={handleDocumentUpload}
                              onDeleteSuccess={handleDocumentDelete}
                              onVerifySuccess={handleDocumentVerify}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Legacy single-string address kept for backward compatibility (hidden) */}
              <input type="hidden" name="registered_address" value={formData.registered_address || ''} onChange={() => {}} />

              {showMoA && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DocumentUpload
                    clientId={id}
                    docType="moa"
                    label="MoA (Memorandum of Association)"
                    documentData={formData.documents?.moa}
                    onUploadSuccess={handleDocumentUpload}
                    onDeleteSuccess={handleDocumentDelete}
                    onVerifySuccess={handleDocumentVerify}
                  />
                  <DocumentUpload
                    clientId={id}
                    docType="aoa"
                    label="AoA (Articles of Association)"
                    documentData={formData.documents?.aoa}
                    onUploadSuccess={handleDocumentUpload}
                    onDeleteSuccess={handleDocumentDelete}
                    onVerifySuccess={handleDocumentVerify}
                  />
                </div>
              )}

              <div className="space-y-2 pt-4 border-t">
                <Label className="text-slate-500">Reviewer Observation</Label>
                <Textarea
                  name="legal_observation"
                  value={formData.legal_observation}
                  onChange={handleChange}
                  placeholder="Add notes or observations about legal entity information..."
                  className="min-h-[80px] bg-yellow-50 border-yellow-200"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 2: Director/Partner Information */}
        {activeSection === 2 && showDirectors && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="text-[#E84C3D]" size={20} />
                {directorLabel} Information
              </CardTitle>
              <CardDescription>Add {directorLabel.toLowerCase()}s and their KYC details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {formData.directors.map((director, index) => (
                <div key={index} className="p-4 border rounded-lg bg-slate-50" data-testid={`director-row-${index}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium flex items-center gap-2">
                      {directorLabel} {index + 1}
                      {director.source === 'mca' && (
                        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`director-source-mca-${index}`}>
                          From MCA
                        </span>
                      )}
                    </h4>
                    {formData.directors.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDirector(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>Name</Label>
                      <Input
                        value={director.name}
                        onChange={(e) => updateDirector(index, 'name', e.target.value)}
                        placeholder="Full name"
                        className="h-11"
                        data-testid={`director-name-${index}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Designation</Label>
                      <Input
                        value={director.designation}
                        onChange={(e) => updateDirector(index, 'designation', e.target.value)}
                        placeholder="Director / Partner / Promoter"
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>DIN</Label>
                      <Input
                        value={director.din || ''}
                        onChange={(e) => updateDirector(index, 'din', e.target.value)}
                        placeholder="8-digit DIN"
                        className="h-11 font-mono"
                        data-testid={`director-din-${index}`}
                      />
                    </div>
                  </div>
                  {director.din_status && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 uppercase">DIN Status:</span>
                      <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                        (director.din_status || '').toLowerCase() === 'active'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-rose-50 text-rose-700 border border-rose-200'
                      }`} data-testid={`director-din-status-${index}`}>
                        {director.din_status}
                      </span>
                    </div>
                  )}
                  <div className="mt-3">
                    <DocumentUpload
                      clientId={id}
                      docType={`kyc_d${index + 1}`}
                      label={`KYC Document - ${director.name || `${directorLabel} ${index + 1}`}`}
                      documentData={formData.documents?.[`kyc_d${index + 1}`]}
                      onUploadSuccess={handleDocumentUpload}
                      onDeleteSuccess={handleDocumentDelete}
                      onVerifySuccess={handleDocumentVerify}
                    />
                  </div>
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={addDirector}
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add {directorLabel}
              </Button>

              <div className="space-y-2 pt-4 border-t">
                <Label className="text-slate-500">Reviewer Observation</Label>
                <Textarea
                  name="director_observation"
                  value={formData.director_observation}
                  onChange={handleChange}
                  placeholder={`Add notes or observations about ${directorLabel.toLowerCase()} information...`}
                  className="min-h-[80px] bg-yellow-50 border-yellow-200"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 3: Registrations & Tax */}
        {activeSection === (showDirectors ? 3 : 2) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="text-[#E84C3D]" size={20} />
                Registrations & Tax
              </CardTitle>
              <CardDescription>PAN, MSME, and GST registration details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>PAN</Label>
                    <Input
                      name="pan"
                      value={formData.pan}
                      onChange={handleChange}
                      placeholder="ABCDE1234F"
                      className="h-11"
                    />
                  </div>
                  <DocumentUpload
                    clientId={id}
                    docType="pan"
                    label="PAN Card"
                    documentData={formData.documents?.pan}
                    onUploadSuccess={handleDocumentUpload}
                    onDeleteSuccess={handleDocumentDelete}
                    onVerifySuccess={handleDocumentVerify}
                  />
                </div>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>MSME Udyam ID</Label>
                    <Input
                      name="msme_udyam_id"
                      value={formData.msme_udyam_id}
                      onChange={handleChange}
                      placeholder="UDYAM-XX-00-0000000"
                      className="h-11"
                    />
                  </div>
                  <DocumentUpload
                    clientId={id}
                    docType="udyam"
                    label="Udyam Registration"
                    documentData={formData.documents?.udyam}
                    onUploadSuccess={handleDocumentUpload}
                    onDeleteSuccess={handleDocumentDelete}
                    onVerifySuccess={handleDocumentVerify}
                  />
                </div>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>GSTIN</Label>
                    <Input
                      name="gstin"
                      value={formData.gstin}
                      onChange={handleChange}
                      placeholder="27ABCDE1234F1Z5"
                      className="h-11"
                    />
                  </div>
                  <DocumentUpload
                    clientId={id}
                    docType="gstin"
                    label="GST Registration"
                    documentData={formData.documents?.gstin}
                    onUploadSuccess={handleDocumentUpload}
                    onDeleteSuccess={handleDocumentDelete}
                    onVerifySuccess={handleDocumentVerify}
                  />
                </div>
              </div>

              {/* ── Additional registrations (Jun 2026) ── */}
              <div className="pt-4 border-t space-y-4" data-testid="additional-registrations-block">
                <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Additional Registrations</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Startup India DPIIT Number</Label>
                      <Input
                        name="startup_india_dpiit_number"
                        value={formData.startup_india_dpiit_number || ''}
                        onChange={handleChange}
                        placeholder="DIPP0000"
                        className="h-11"
                        data-testid="startup-india-input"
                      />
                    </div>
                    <DocumentUpload
                      clientId={id}
                      docType="dpiit"
                      label="Startup India Certificate"
                      documentData={formData.documents?.dpiit}
                      onUploadSuccess={handleDocumentUpload}
                      onDeleteSuccess={handleDocumentDelete}
                      onVerifySuccess={handleDocumentVerify}
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>FSSAI Number</Label>
                      <Input
                        name="fssai_number"
                        value={formData.fssai_number || ''}
                        onChange={handleChange}
                        placeholder="14-digit FSSAI licence"
                        className="h-11"
                        data-testid="fssai-input"
                      />
                    </div>
                    <DocumentUpload
                      clientId={id}
                      docType="fssai"
                      label="FSSAI Certificate"
                      documentData={formData.documents?.fssai}
                      onUploadSuccess={handleDocumentUpload}
                      onDeleteSuccess={handleDocumentDelete}
                      onVerifySuccess={handleDocumentVerify}
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>ESIC Number</Label>
                      <Input
                        name="esic_number"
                        value={formData.esic_number || ''}
                        onChange={handleChange}
                        placeholder="17-digit ESIC code"
                        className="h-11"
                        data-testid="esic-input"
                      />
                    </div>
                    <DocumentUpload
                      clientId={id}
                      docType="esic"
                      label="ESIC Registration"
                      documentData={formData.documents?.esic}
                      onUploadSuccess={handleDocumentUpload}
                      onDeleteSuccess={handleDocumentDelete}
                      onVerifySuccess={handleDocumentVerify}
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Shop &amp; Establishment Number</Label>
                      <Input
                        name="shop_establishment_number"
                        value={formData.shop_establishment_number || ''}
                        onChange={handleChange}
                        placeholder="State-specific licence no."
                        className="h-11"
                        data-testid="shop-establishment-input"
                      />
                    </div>
                    <DocumentUpload
                      clientId={id}
                      docType="shop_establishment"
                      label="Shop &amp; Establishment Certificate"
                      documentData={formData.documents?.shop_establishment}
                      onUploadSuccess={handleDocumentUpload}
                      onDeleteSuccess={handleDocumentDelete}
                      onVerifySuccess={handleDocumentVerify}
                    />
                  </div>
                </div>

                {/* Dynamic "Other" list */}
                <div className="pt-2" data-testid="other-registrations-block">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-slate-500">Other Registrations</Label>
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => setFormData((f) => ({
                        ...f,
                        other_registrations: [...(f.other_registrations || []), { label: '', number: '' }],
                      }))}
                      data-testid="add-other-reg-btn">
                      <Plus size={12} className="mr-1" /> Add Other
                    </Button>
                  </div>
                  {(formData.other_registrations || []).length === 0 ? (
                    <p className="text-xs text-slate-400 italic">e.g. Trade licence, IEC, Drug licence, ISO cert.</p>
                  ) : (
                    <div className="space-y-2">
                      {formData.other_registrations.map((r, i) => (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center" data-testid={`other-reg-row-${i}`}>
                          <Input placeholder="Registration name (e.g. IEC)"
                            value={r.label || ''}
                            onChange={(e) => setFormData((f) => ({
                              ...f,
                              other_registrations: f.other_registrations.map((x, j) => j === i ? { ...x, label: e.target.value } : x),
                            }))}
                            className="md:col-span-2"
                            data-testid={`other-reg-label-${i}`}
                          />
                          <Input placeholder="Registration number"
                            value={r.number || ''}
                            onChange={(e) => setFormData((f) => ({
                              ...f,
                              other_registrations: f.other_registrations.map((x, j) => j === i ? { ...x, number: e.target.value } : x),
                            }))}
                            className="md:col-span-2"
                            data-testid={`other-reg-number-${i}`}
                          />
                          <Button type="button" size="sm" variant="ghost" className="text-rose-500"
                            onClick={() => setFormData((f) => ({
                              ...f,
                              other_registrations: f.other_registrations.filter((_, j) => j !== i),
                            }))}
                            data-testid={`other-reg-remove-${i}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label className="text-slate-500">Reviewer Observation</Label>
                <Textarea
                  name="registration_observation"
                  value={formData.registration_observation}
                  onChange={handleChange}
                  placeholder="Add notes or observations about registrations..."
                  className="min-h-[80px] bg-yellow-50 border-yellow-200"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 4: Financial Documents */}
        {activeSection === (showDirectors ? 4 : 3) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="text-[#E84C3D]" size={20} />
                Financial Documents
              </CardTitle>
              <CardDescription>Annual reports, ITRs, and bank statements</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Last Completed Financial Year</Label>
                <Select value={formData.last_completed_fy} onValueChange={(v) => handleSelectChange('last_completed_fy', v)}>
                  <SelectTrigger className="h-11 max-w-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FINANCIAL_YEARS.map(fy => (
                      <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[0, 1, 2].map(yearOffset => {
                  const baseYear = parseInt(formData.last_completed_fy?.split('-')[0] || '2023');
                  const fy = `${baseYear - yearOffset}-${(baseYear - yearOffset + 1).toString().slice(-2)}`;
                  const yearKey = `y${yearOffset + 1}`;
                  const yearBanks = formData.banks?.[yearKey] || [{ name: '', account_number: '' }];
                  return (
                    <div key={yearOffset} className="p-4 border rounded-lg bg-slate-50">
                      <h4 className="font-medium mb-3">FY {fy}</h4>
                      <div className="space-y-3">
                        <DocumentUpload
                          clientId={id}
                          docType={`ar_${yearKey}`}
                          label={`Annual Report ${fy}`}
                          documentData={formData.documents?.[`ar_${yearKey}`]}
                          onUploadSuccess={handleDocumentUpload}
                          onDeleteSuccess={handleDocumentDelete}
                          onVerifySuccess={handleDocumentVerify}
                        />
                        <DocumentUpload
                          clientId={id}
                          docType={`itr_${yearKey}`}
                          label={`ITR ${fy}`}
                          documentData={formData.documents?.[`itr_${yearKey}`]}
                          onUploadSuccess={handleDocumentUpload}
                          onDeleteSuccess={handleDocumentDelete}
                          onVerifySuccess={handleDocumentVerify}
                        />
                        
                        {/* Dynamic Bank Statements */}
                        <div className="pt-2 border-t border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 mb-2">BANK STATEMENTS</p>
                          {yearBanks.map((bank, bankIndex) => (
                            <div key={bankIndex} className="mb-3 p-3 bg-white rounded border">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-slate-600">Bank {bankIndex + 1}</span>
                                {yearBanks.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeBank(yearKey, bankIndex)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                              <Input
                                placeholder="Bank Name"
                                value={bank.name}
                                onChange={(e) => updateBank(yearKey, bankIndex, 'name', e.target.value)}
                                className="h-9 text-sm mb-2"
                              />
                              <Input
                                placeholder="Account Number"
                                value={bank.account_number}
                                onChange={(e) => updateBank(yearKey, bankIndex, 'account_number', e.target.value)}
                                className="h-9 text-sm mb-2"
                              />
                              <DocumentUpload
                                clientId={id}
                                docType={`bank_${yearKey}_${bankIndex + 1}`}
                                label={`Statement - ${bank.name || `Bank ${bankIndex + 1}`}`}
                                documentData={formData.documents?.[`bank_${yearKey}_${bankIndex + 1}`]}
                                onUploadSuccess={handleDocumentUpload}
                                onDeleteSuccess={handleDocumentDelete}
                                onVerifySuccess={handleDocumentVerify}
                              />
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => addBank(yearKey)}
                            className="w-full text-xs"
                          >
                            <Plus size={14} className="mr-1" />
                            Add Another Bank
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label className="text-slate-500">Reviewer Observation</Label>
                <Textarea
                  name="financial_observation"
                  value={formData.financial_observation}
                  onChange={handleChange}
                  placeholder="Add notes or observations about financial documents..."
                  className="min-h-[80px] bg-yellow-50 border-yellow-200"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 5: Contact Information */}
        {activeSection === (showDirectors ? 5 : 4) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="text-[#E84C3D]" size={20} />
                Contact Information
              </CardTitle>
              <CardDescription>Primary point of contact details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Point of Contact Name *</Label>
                  <Input
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    placeholder="Full name"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="email@company.com"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="h-11"
                  />
                </div>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label className="text-slate-500">Reviewer Observation</Label>
                <Textarea
                  name="contact_observation"
                  value={formData.contact_observation}
                  onChange={handleChange}
                  placeholder="Add notes or observations about contact..."
                  className="min-h-[80px] bg-yellow-50 border-yellow-200"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 6: Tally Prime Setup */}
        {activeSection === (showDirectors ? 6 : 5) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="text-[#E84C3D]" size={20} />
                Tally Prime Cloud Setup
              </CardTitle>
              <CardDescription>Track setup progress for Tally integrations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { key: 'tally_cloud_setup', label: 'Tally Prime Cloud Setup' },
                  { key: 'chart_of_accounts_setup', label: 'Chart of Accounts Setup' },
                  { key: 'import_trial_balance', label: 'Import Last FY Trial Balance' },
                  { key: 'payment_link_setup', label: 'Payment Link Setup' },
                  { key: 'bank_integration_setup', label: 'Bank Integration Setup' },
                  { key: 'tally_dashboard_setup', label: 'Tally Dashboard Setup' },
                  { key: 'whatsapp_integration', label: 'WhatsApp Integration' },
                  { key: 'gst_filing_setup', label: 'GST Filing Setup' },
                  { key: 'tds_filing_setup', label: 'TDS Filing Setup' },
                ].map(item => (
                  <div key={item.key} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50">
                    <Checkbox
                      id={item.key}
                      checked={formData[item.key]}
                      onCheckedChange={(checked) => handleCheckboxChange(item.key, checked)}
                    />
                    <label
                      htmlFor={item.key}
                      className="text-sm font-medium cursor-pointer flex-1"
                    >
                      {item.label}
                    </label>
                    <span className={`text-xs px-2 py-0.5 rounded ${formData[item.key] ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {formData[item.key] ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label className="text-slate-500">Reviewer Observation</Label>
                <Textarea
                  name="tally_observation"
                  value={formData.tally_observation}
                  onChange={handleChange}
                  placeholder="Add notes or observations about Tally setup..."
                  className="min-h-[80px] bg-yellow-50 border-yellow-200"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bottom Actions */}
        <div className="flex justify-between items-center mt-6 pt-6 border-t">
          <div className="flex gap-2">
            {activeSection > 0 && (
              <Button type="button" variant="outline" onClick={() => setActiveSection(prev => prev - 1)}>
                Previous
              </Button>
            )}
            {activeSection < sections.length - 1 && (
              <Button type="button" variant="outline" onClick={() => setActiveSection(prev => prev + 1)}>
                Next
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Link to="/admin/clients">
              <Button type="button" variant="ghost">Cancel</Button>
            </Link>
            <Button 
              type="button" 
              variant="outline"
              onClick={(e) => handleSubmit(e, 'draft')}
              disabled={loading}
            >
              Save as Draft
            </Button>
            <Button 
              type="button"
              className="bg-[#059669] hover:bg-[#047857]"
              onClick={(e) => handleSubmit(e, 'complete')}
              disabled={loading}
            >
              <Save size={18} className="mr-2" />
              Mark Complete
            </Button>
          </div>
        </div>
      </form>

      {/* ─── Setup-not-complete Readiness Dialog (Jun 2026 rebuild) ─── */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="max-w-lg" data-testid="setup-readiness-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <DialogTitle>Setup not complete</DialogTitle>
                <DialogDescription>
                  Resolve the {setupIssues.length} outstanding item{setupIssues.length !== 1 ? 's' : ''} below, then click Mark Complete again.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="divide-y divide-slate-100 border rounded-md" data-testid="setup-issues-list">
            {setupIssues.map((issue) => (
              <div key={issue.key} className="p-3 flex items-center justify-between gap-3" data-testid={`setup-issue-${issue.key}`}>
                <span className="text-sm text-slate-800">{issue.label}</span>
                <button
                  type="button"
                  onClick={() => navigateToTarget(issue)}
                  className="text-xs font-semibold text-[#0f766e] hover:text-[#065f46] flex items-center gap-1 whitespace-nowrap"
                  data-testid={`setup-issue-goto-${issue.key}`}
                >
                  Take me there <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)} data-testid="setup-dialog-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
